import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Upload, X, CheckCircle2, AlertCircle, Loader2, 
  Watch, Moon, Zap, Activity, Heart, Footprints,
  ChevronRight, Edit2, Save, RotateCcw, Trash2,
  ArrowLeft, History, TrendingUp
} from 'lucide-react';

// Device and app source options
const DEVICE_TYPES = [
  { value: 'amazfit_helio', label: 'Amazfit Helio Strap' },
  { value: 'apple_watch', label: 'Apple Watch' },
  { value: 'garmin', label: 'Garmin' },
  { value: 'oura', label: 'Oura Ring' },
  { value: 'fitbit', label: 'Fitbit' },
  { value: 'whoop', label: 'WHOOP' },
  { value: 'other', label: 'Other' },
];

const APP_SOURCES = [
  { value: 'zepp', label: 'Zepp' },
  { value: 'apple_health', label: 'Apple Health' },
  { value: 'garmin_connect', label: 'Garmin Connect' },
  { value: 'oura_app', label: 'Oura App' },
  { value: 'fitbit_app', label: 'Fitbit App' },
  { value: 'whoop_app', label: 'WHOOP App' },
  { value: 'other', label: 'Other' },
];

const SCREENSHOT_TYPES = [
  { value: 'overview', label: 'Overview', icon: Watch, color: 'bg-blue-500/20 text-blue-400' },
  { value: 'sleep', label: 'Sleep', icon: Moon, color: 'bg-indigo-500/20 text-indigo-400' },
  { value: 'biocharge', label: 'Biocharge', icon: Zap, color: 'bg-amber-500/20 text-amber-400' },
  { value: 'exertion', label: 'Exertion', icon: Activity, color: 'bg-rose-500/20 text-rose-400' },
  { value: 'workout', label: 'Workout', icon: Footprints, color: 'bg-emerald-500/20 text-emerald-400' },
];

// Metric field definitions
const METRIC_FIELDS = {
  sleep_duration: { label: 'Total Sleep', unit: 'hrs', type: 'number', step: 0.1, min: 0, max: 24 },
  sleep_score: { label: 'Sleep Score', unit: '%', type: 'number', step: 1, min: 0, max: 100 },
  deep_sleep: { label: 'Deep Sleep', unit: 'hrs', type: 'number', step: 0.1, min: 0, max: 12 },
  rem_sleep: { label: 'REM Sleep', unit: 'hrs', type: 'number', step: 0.1, min: 0, max: 12 },
  awakenings: { label: 'Awakenings', unit: '', type: 'number', step: 1, min: 0, max: 50 },
  hrv: { label: 'HRV', unit: 'ms', type: 'number', step: 1, min: 0, max: 300 },
  resting_hr: { label: 'Resting HR', unit: 'bpm', type: 'number', step: 1, min: 30, max: 150 },
  recovery_score: { label: 'Recovery', unit: '%', type: 'number', step: 1, min: 0, max: 100 },
  biocharge: { label: 'Biocharge', unit: '%', type: 'number', step: 1, min: 0, max: 100 },
  steps: { label: 'Steps', unit: '', type: 'number', step: 1, min: 0, max: 100000 },
  exertion: { label: 'Exertion', unit: '', type: 'number', step: 0.1, min: 0, max: 100 },
  workout_type: { label: 'Workout Type', unit: '', type: 'text' },
  workout_duration: { label: 'Workout Duration', unit: 'min', type: 'number', step: 1, min: 0, max: 600 },
  avg_hr: { label: 'Avg Heart Rate', unit: 'bpm', type: 'number', step: 1, min: 30, max: 220 },
  max_hr: { label: 'Max Heart Rate', unit: 'bpm', type: 'number', step: 1, min: 30, max: 250 },
  calories: { label: 'Calories', unit: 'kcal', type: 'number', step: 1, min: 0, max: 10000 },
};

const CONFIDENCE_LEVELS = {
  high: { label: 'High', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  medium: { label: 'Medium', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  low: { label: 'Low', color: 'text-rose-400', bg: 'bg-rose-500/20' },
};

function getConfidenceLevel(score) {
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

// Upload item component (same as subscriber version)
function UploadItem({ file, index, extraction, onRemove, onTypeChange, onMetricChange, isProcessing }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [localMetrics, setLocalMetrics] = useState({});
  
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);
  
  useEffect(() => {
    if (extraction?.metrics) {
      setLocalMetrics(extraction.metrics);
    }
  }, [extraction?.metrics]);
  
  const handleMetricChange = (key, value) => {
    const field = METRIC_FIELDS[key];
    let parsedValue = value;
    if (field?.type === 'number' && value !== '') {
      parsedValue = parseFloat(value);
      if (isNaN(parsedValue)) parsedValue = null;
    }
    setLocalMetrics(prev => ({ ...prev, [key]: parsedValue }));
    onMetricChange(index, key, parsedValue);
  };
  
  const confidence = extraction?.confidence || 0;
  const confidenceLevel = getConfidenceLevel(confidence);
  const confidenceInfo = CONFIDENCE_LEVELS[confidenceLevel];
  
  const getRelevantMetrics = () => {
    const type = extraction?.screenshot_type;
    switch (type) {
      case 'sleep':
        return ['sleep_duration', 'sleep_score', 'deep_sleep', 'rem_sleep', 'awakenings'];
      case 'biocharge':
        return ['recovery_score', 'biocharge', 'hrv', 'resting_hr'];
      case 'exertion':
        return ['exertion', 'steps', 'calories'];
      case 'workout':
        return ['workout_type', 'workout_duration', 'avg_hr', 'max_hr', 'calories'];
      default:
        return ['hrv', 'resting_hr', 'steps', 'sleep_score', 'recovery_score'];
    }
  };
  
  const relevantMetrics = getRelevantMetrics();
  
  return (
    <Card className="bg-[#131B2B] border-white/10 overflow-hidden">
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-1/3 relative">
          {previewUrl && (
            <img 
              src={previewUrl} 
              alt={`Screenshot ${index + 1}`}
              className="w-full h-48 lg:h-full object-cover"
            />
          )}
          {isProcessing && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-[#C89B7B] animate-spin mx-auto mb-2" />
                <p className="text-white/70 text-sm">Analyzing...</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white"
            onClick={() => onRemove(index)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="lg:w-2/3 p-4">
          {!extraction ? (
            <div className="h-full flex items-center justify-center text-white/50">
              <p>Waiting for analysis...</p>
            </div>
          ) : extraction.error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
                <p className="text-rose-400 text-sm">{extraction.error}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Select 
                    value={extraction.screenshot_type || 'overview'} 
                    onValueChange={(val) => onTypeChange(index, val)}
                  >
                    <SelectTrigger className="w-36 bg-[#0B0F19] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131B2B] border-white/10">
                      {SCREENSHOT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-white hover:bg-adm-surface/10">
                          <div className="flex items-center gap-2">
                            <type.icon className="w-4 h-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Badge className={`${confidenceInfo.bg} ${confidenceInfo.color} border-0`}>
                    {Math.round(confidence * 100)}% confidence
                  </Badge>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white/70 hover:text-white"
                  onClick={() => setIsEditing(!isEditing)}
                >
                  {isEditing ? <Save className="w-4 h-4 mr-1" /> : <Edit2 className="w-4 h-4 mr-1" />}
                  {isEditing ? 'Done' : 'Edit'}
                </Button>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {relevantMetrics.map(key => {
                  const field = METRIC_FIELDS[key];
                  const value = localMetrics[key];
                  const hasValue = value !== null && value !== undefined && value !== '';
                  
                  return (
                    <div key={key} className="space-y-1">
                      <Label className="text-white/50 text-xs">{field.label}</Label>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type={field.type}
                            step={field.step}
                            min={field.min}
                            max={field.max}
                            value={value ?? ''}
                            onChange={(e) => handleMetricChange(key, e.target.value)}
                            className="h-8 bg-[#0B0F19] border-white/20 text-white text-sm"
                            placeholder="—"
                          />
                          {field.unit && <span className="text-white/40 text-xs">{field.unit}</span>}
                        </div>
                      ) : (
                        <div className={`text-sm font-medium ${hasValue ? 'text-white' : 'text-white/30'}`}>
                          {hasValue ? `${value}${field.unit ? ` ${field.unit}` : ''}` : '—'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {confidenceLevel === 'low' && (
                <div className="flex items-start gap-2 p-2 bg-rose-500/10 rounded-lg border border-rose-500/20">
                  <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                  <p className="text-rose-400 text-xs">
                    Low confidence extraction. Please verify and correct values.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function AdminWearablesPage() {
  const { subscriberId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // State
  const [activeTab, setActiveTab] = useState('upload');
  const [subscriber, setSubscriber] = useState(null);
  const [records, setRecords] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Upload state
  const [deviceType, setDeviceType] = useState('amazfit_helio');
  const [appSource, setAppSource] = useState('zepp');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [uploads, setUploads] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Fetch subscriber and wearable data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, recordsRes, trendsRes] = await Promise.all([
          axios.get(`${API}/users/${subscriberId}`),
          axios.get(`${API}/wearables/${subscriberId}?days=30`),
          axios.get(`${API}/wearables/${subscriberId}/trends?days=14`),
        ]);
        setSubscriber(userRes.data);
        setRecords(recordsRes.data);
        setTrends(trendsRes.data);
      } catch (error) {
        console.error('Fetch error:', error);
        toast.error('Failed to load wearable data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [subscriberId]);
  
  // File handling
  const handleFileSelect = useCallback((files) => {
    const validFiles = Array.from(files).filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ['png', 'jpg', 'jpeg'].includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error('Please select PNG or JPG images only');
      return;
    }
    
    const newUploads = validFiles.map(file => ({
      file,
      extraction: null,
      processing: false,
    }));
    
    setUploads(prev => [...prev, ...newUploads]);
  }, []);
  
  const handleDragEnter = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };
  
  // Process uploads
  const processUploads = async () => {
    setUploads(prev => prev.map(u => !u.extraction ? { ...u, processing: true } : u));
    
    for (let i = 0; i < uploads.length; i++) {
      if (uploads[i].extraction) continue;
      
      try {
        const formData = new FormData();
        formData.append('file', uploads[i].file);
        
        const response = await axios.post(
          `${API}/wearables/${subscriberId}/analyze?device_type=${deviceType}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setUploads(prev => prev.map((u, idx) => 
          idx === i ? { ...u, processing: false, extraction: response.data.extraction || { error: 'No data' } } : u
        ));
      } catch (error) {
        setUploads(prev => prev.map((u, idx) => 
          idx === i ? { ...u, processing: false, extraction: { error: 'Analysis failed' } } : u
        ));
      }
    }
  };
  
  const handleRemove = (index) => setUploads(prev => prev.filter((_, i) => i !== index));
  
  const handleTypeChange = (index, newType) => {
    setUploads(prev => prev.map((u, i) => 
      i === index ? { ...u, extraction: { ...u.extraction, screenshot_type: newType } } : u
    ));
  };
  
  const handleMetricChange = (index, key, value) => {
    setUploads(prev => prev.map((u, i) => 
      i === index ? { ...u, extraction: { ...u.extraction, metrics: { ...u.extraction?.metrics, [key]: value } } } : u
    ));
  };
  
  // Save
  const handleSave = async () => {
    const validUploads = uploads.filter(u => u.extraction && !u.extraction.error);
    if (validUploads.length === 0) {
      toast.error('No valid data to save');
      return;
    }
    
    setIsSaving(true);
    
    try {
      for (const upload of validUploads) {
        const { extraction } = upload;
        const cleanMetrics = {};
        Object.entries(extraction.metrics || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '') cleanMetrics[k] = v;
        });
        
        await axios.post(`${API}/wearables/${subscriberId}/save`, {
          date,
          device_type: deviceType,
          app_ecosystem: appSource,
          screenshot_type: extraction.screenshot_type || 'overview',
          metrics: cleanMetrics,
          source: 'screenshot_ai',
          confidence_score: extraction.confidence || 0.5,
          notes: notes || null,
        });
      }
      
      toast.success(`Saved ${validUploads.length} wearable record(s)`);
      setUploads([]);
      setNotes('');
      
      // Refresh records
      const recordsRes = await axios.get(`${API}/wearables/${subscriberId}?days=30`);
      setRecords(recordsRes.data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };
  
  const isProcessing = uploads.some(u => u.processing);
  const hasUploads = uploads.length > 0;
  const allProcessed = uploads.every(u => u.extraction);
  const validCount = uploads.filter(u => u.extraction && !u.extraction.error).length;
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#C89B7B] animate-spin" />
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-wearables-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-['Outfit'] font-bold text-white">
              Wearable Intelligence
            </h1>
            <p className="text-white/50 text-sm">
              {subscriber?.name} • Biological signal capture and validation
            </p>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#131B2B] border border-white/10">
            <TabsTrigger value="upload" className="data-[state=active]:bg-[#C89B7B]/20 data-[state=active]:text-[#C89B7B]">
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-[#C89B7B]/20 data-[state=active]:text-[#C89B7B]">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-[#C89B7B]/20 data-[state=active]:text-[#C89B7B]">
              <TrendingUp className="w-4 h-4 mr-2" />
              Trends
            </TabsTrigger>
          </TabsList>
          
          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            {/* Metadata */}
            <Card className="bg-[#131B2B] border-white/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-white text-lg">Device & Date</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/70">Device Type</Label>
                    <Select value={deviceType} onValueChange={setDeviceType}>
                      <SelectTrigger className="bg-[#0B0F19] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#131B2B] border-white/10">
                        {DEVICE_TYPES.map(d => (
                          <SelectItem key={d.value} value={d.value} className="text-white hover:bg-adm-surface/10">
                            {d.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">App Source</Label>
                    <Select value={appSource} onValueChange={setAppSource}>
                      <SelectTrigger className="bg-[#0B0F19] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#131B2B] border-white/10">
                        {APP_SOURCES.map(a => (
                          <SelectItem key={a.value} value={a.value} className="text-white hover:bg-adm-surface/10">
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/70">Date</Label>
                    <Input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="bg-[#0B0F19] border-white/10 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Drop zone */}
            <div
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging ? 'border-[#C89B7B] bg-[#C89B7B]/5' : 'border-white/20 hover:border-white/30 bg-[#131B2B]'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
              />
              <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragging ? 'text-[#C89B7B]' : 'text-white/40'}`} />
              <p className="text-white font-medium mb-1">Drop screenshots here</p>
              <p className="text-white/50 text-sm mb-3">PNG or JPG images</p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-white/20 text-white hover:bg-adm-surface/5"
              >
                Select Files
              </Button>
            </div>
            
            {/* Uploads */}
            {hasUploads && (
              <div className="space-y-4">
                {uploads.map((upload, i) => (
                  <UploadItem
                    key={i}
                    index={i}
                    file={upload.file}
                    extraction={upload.extraction}
                    isProcessing={upload.processing}
                    onRemove={handleRemove}
                    onTypeChange={handleTypeChange}
                    onMetricChange={handleMetricChange}
                  />
                ))}
                
                {/* Notes */}
                {allProcessed && (
                  <Card className="bg-[#131B2B] border-white/10">
                    <CardContent className="pt-6">
                      <Label className="text-white/70 mb-2 block">Notes (optional)</Label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes..."
                        className="bg-[#0B0F19] border-white/10 text-white resize-none"
                        rows={2}
                      />
                    </CardContent>
                  </Card>
                )}
                
                {/* Actions */}
                <div className="flex gap-3">
                  {!allProcessed ? (
                    <Button
                      onClick={processUploads}
                      disabled={isProcessing}
                      className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
                    >
                      {isProcessing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                      ) : (
                        <>Analyze Screenshots</>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSave}
                      disabled={isSaving || validCount === 0}
                      className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
                    >
                      {isSaving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                      ) : (
                        <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve & Save</>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => setUploads([])}
                    className="border-white/20 text-white hover:bg-adm-surface/5"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history">
            <Card className="bg-[#131B2B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Wearable Records</CardTitle>
                <CardDescription className="text-white/50">Last 30 days of validated data</CardDescription>
              </CardHeader>
              <CardContent>
                {records.length === 0 ? (
                  <p className="text-white/50 text-center py-8">No wearable records yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-white/50">Date</TableHead>
                        <TableHead className="text-white/50">Type</TableHead>
                        <TableHead className="text-white/50">Device</TableHead>
                        <TableHead className="text-white/50">Key Metrics</TableHead>
                        <TableHead className="text-white/50">Confidence</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {records.map((record, i) => {
                        const typeInfo = SCREENSHOT_TYPES.find(t => t.value === record.screenshot_type);
                        const metrics = record.metrics || {};
                        const keyMetrics = Object.entries(metrics).slice(0, 3).map(([k, v]) => {
                          const field = METRIC_FIELDS[k];
                          return field ? `${field.label}: ${v}${field.unit ? ` ${field.unit}` : ''}` : null;
                        }).filter(Boolean);
                        
                        return (
                          <TableRow key={record.id || i} className="border-white/5 hover:bg-adm-surface/5">
                            <TableCell className="text-white font-medium">{record.date}</TableCell>
                            <TableCell>
                              <Badge className={typeInfo?.color || 'bg-adm-surface/10 text-white'}>
                                {typeInfo?.label || record.screenshot_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-white/70">{record.device_type}</TableCell>
                            <TableCell className="text-white/70 text-sm">{keyMetrics.join(' • ')}</TableCell>
                            <TableCell>
                              <span className={`text-sm ${
                                (record.confidence_score || 0) >= 0.8 ? 'text-emerald-400' :
                                (record.confidence_score || 0) >= 0.5 ? 'text-amber-400' : 'text-rose-400'
                              }`}>
                                {Math.round((record.confidence_score || 0) * 100)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Trends Tab */}
          <TabsContent value="trends">
            <Card className="bg-[#131B2B] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">14-Day Trends</CardTitle>
                <CardDescription className="text-white/50">Aggregated wearable metrics</CardDescription>
              </CardHeader>
              <CardContent>
                {!trends?.trends || Object.keys(trends.trends).length === 0 ? (
                  <p className="text-white/50 text-center py-8">Not enough data for trends</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {Object.entries(trends.trends).map(([key, data]) => {
                      const field = METRIC_FIELDS[key];
                      if (!field) return null;
                      
                      return (
                        <div key={key} className="bg-[#0B0F19] rounded-lg p-4 border border-white/5">
                          <p className="text-white/50 text-xs mb-1">{field.label}</p>
                          <p className="text-white text-xl font-medium">
                            {data.avg}{field.unit ? ` ${field.unit}` : ''}
                          </p>
                          <p className="text-white/30 text-xs mt-1">
                            Range: {data.min} - {data.max}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
